import { Store } from "@/store";
import { initApi } from "@/utils";

/**
 * Simplifies the given context using AI-powered summarization.
 *
 * @param {string} context - The text to be simplified.
 * @returns {Promise<string>} A promise that resolves to the simplified text.
 *   The response can be in both Markdown and HTML formats.
 * @throws {Error} If there's an issue with the API request or if required data is missing.
 */

export async function simplifyAsync(context: string): Promise<string> {
  const api = initApi();
  const projectId = Store.project.id.value;
  const trackId = getTrackId();

  if (!projectId || !trackId) {
    throw new Error("Missing required data for AI request");
  }
  try {
    const res = await api.summarizeProject({
      id: projectId,
      summarizeProjectRequest: { trackId, highlightedText: context },
    });

    return res.summary || "";
  } catch (error) {
    console.error("Error fetching AI-generated text:", error);
    throw error;
  }
}

/**
 * Explains the given context using AI-powered explanation.
 *
 * @param {string} context - The text to be explained.
 * @returns {Promise<string>} A promise that resolves to the explained text.
 *   The response can be in both Markdown and HTML formats.
 * @throws {Error} If there's an issue with the API request or if required data is missing.
 */
export async function explainAsync(context: string): Promise<string> {
  const api = initApi();
  const projectId = Store.project.id.value;
  const trackId = getTrackId();

  if (!projectId || !trackId) {
    throw new Error("Missing required data for AI request");
  }
  try {
    const res = await api.explainProject({
      id: projectId,
      explainProjectRequest: { trackId, highlightedText: context },
    });

    return res.explanation || "";
  } catch (error) {
    console.error("Error fetching AI-generated text:", error);
    throw error;
  }
}

/**
 * Gives examples for the given context using AI-powered examples.
 *
 * @param {string} context - The text to be explained.
 * @returns {Promise<string>} A promise that resolves to the explained text.
 *   The response can be in both Markdown and HTML formats.
 * @throws {Error} If there's an issue with the API request or if required data is missing.
 */
export async function examplesAsync(context: string): Promise<string> {
  const api = initApi();
  const projectId = Store.project.id.value;
  const trackId = getTrackId();

  if (!projectId || !trackId) {
    throw new Error("Missing required data for AI request");
  }
  try {
    const res = await api.examplesProject({
      id: projectId,
      examplesProjectRequest: { trackId, highlightedText: context },
    });

    return res.examples || "";
  } catch (error) {
    console.error("Error fetching AI-generated text:", error);
    throw error;
  }
}

/**
 * Ask the AI for a response to the given query.
 *
 * @param {string} context - The text to be asked about.
 * @param {string} query - The query to ask the AI.
 * @returns {Promise<string>} A promise that resolves to the AI response.
 * @throws {Error} If there's an issue with the API request or if required data is missing.
 */
export async function askAsync(context: string, query: string): Promise<string> {
  const api = initApi();
  const projectId = Store.project.id.value;
  const trackId = getTrackId();

  if (!projectId || !trackId) {
    throw new Error("Missing required data for AI request");
  }

  try {
    const res = await api.askProject({
      id: projectId,
      askProjectRequest: { trackId, highlightedText: context, query },
    });

    return res.answer || "";
  } catch (error) {
    console.error("Error fetching AI-generated text:", error);
    throw error;
  }
}

function getTrackId(): string | undefined {
  if (Store.editor.activeInstanceId.value === "intro") {
    return "intro";
  }

  return Store.project.getSelectedTrackId();
}
